// pages/master-data/vehicle/VehicleTypeAddEdit.jsx
'use client';
import { useState, useEffect, forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, CircularProgress, Grid, TextField, Divider, Stack, Button, InputAdornment } from '@mui/material';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { getVehicleTypeById, createVehicleType, updateVehicleType } from '../../../stores/vehicleTypeStore';
import { useCompanyDataList } from '../../../stores/companyStore';
import usePickerPagination from '../../../hooks/usePickerPagination';
import FormDialog from '../../../components/FormDialog';
import { useNotifier } from '../../../components/Notifier';
import PickerField from '../../../components/PickerField';
import MasterCompanyAddEdit from '../company/MasterCompanyAddEdit';
import { formatUpperCase } from '../../../utils/formatTitleCase';
import { useLanguage } from '../../../utils/i18n';

const VehicleTypeAddEdit = forwardRef(
  ({ open, id, onClose, onSuccess, onError, onSubmitStart = () => {}, onSubmitEnd = () => {} }, ref) => {
    const { t } = useLanguage();
    const notify = useNotifier();
    const [initialValues, setInitialValues] = useState({
      company: null,
      name: '',
      length_dimension: '',
      width_dimension: '',
      height_dimension: '',
      load_capacity: '',
      max_speed: '',
      min_speed: '',
      average_speed: '',
      description: ''
    });

    const [loading, setLoading] = useState(false);
    const formikRef = useRef(null);

    const companyPickerParams = usePickerPagination({
      fetchHook: useCompanyDataList,
      buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
      searchKey: 'name'
    });

    const mappedCompanyOptions = companyPickerParams.options;

    useEffect(() => {
      try {
        const storedCompanyId = localStorage.getItem('companyId');
        if (!storedCompanyId) return;
        if (!mappedCompanyOptions || mappedCompanyOptions.length === 0) return;

        const matched = mappedCompanyOptions.find((opt) => String(opt.value) === String(storedCompanyId));
        const valueToSet = matched ? matched.value : storedCompanyId;

        if (formikRef.current && typeof formikRef.current.setFieldValue === 'function') {
          formikRef.current.setFieldValue('company', valueToSet);
        }
        setInitialValues((prev) => ({
          ...prev,
          company: prev.company || valueToSet
        }));
      } catch (err) {
        console.error('init company from localStorage failed', err);
      }
    }, [mappedCompanyOptions]);

    useImperativeHandle(ref, () => ({
      submitForm: () => formikRef.current?.submitForm?.()
    }));

    useEffect(() => {
      let mounted = true;
      const fetchInitial = async () => {
        if (!open) return;
        setLoading(true);
        try {
          if (id) {
            const response = await getVehicleTypeById(id);
            if (!mounted) return;
            // Penanganan bila API membungkus data di dalam res.data
            const data = response?.data?.data || response?.data || response;
            if (data) {
              setInitialValues({
                company: (data.company ?? data.company_id) != null ? String(data.company ?? data.company_id) : null,
                name: data.name ?? '',
                length_dimension: data.length_dimension ?? data.length ?? data.lengthDimension ?? '',
                width_dimension: data.width_dimension ?? data.width ?? data.widthDimension ?? '',
                height_dimension: data.height_dimension ?? data.height ?? data.heightDimension ?? '',
                load_capacity: data.load_capacity ?? '',
                max_speed: data.max_speed ?? '',
                min_speed: data.min_speed ?? '',
                average_speed: data.average_speed ?? '',
                description: data.description ?? ''
              });
            }
          } else {
            setInitialValues((prev) => ({
              company: prev.company ?? null,
              name: '',
              length_dimension: '',
              width_dimension: '',
              height_dimension: '',
              load_capacity: '',
              max_speed: '',
              min_speed: '',
              average_speed: '',
              description: ''
            }));
          }
        } catch (err) {
          console.error('Failed to fetch vehicle type:', err);
          onError && onError('Failed to load data');
        } finally {
          if (mounted) setLoading(false);
        }
      };
      fetchInitial();
      return () => {
        mounted = false;
      };
    }, [id, onError, open]);

    // --- PERBAIKAN VALIDASI YUP ---
    const validationSchema = Yup.object().shape({
      company: Yup.mixed().required('Company is required'),
      name: Yup.string().required('Name is required'),
      length_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number'),
      width_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number'),
      height_dimension: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number'),
      load_capacity: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number'),

      min_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-less-than-max', 'Min Speed cannot be greater than Max Speed', function (value) {
          const { max_speed } = this.parent;
          if (value == null || max_speed == null || max_speed === '') return true; // Lewati jika kosong
          return value <= max_speed;
        }),

      max_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-greater-than-min', 'Max Speed cannot be less than Min Speed', function (value) {
          const { min_speed } = this.parent;
          if (value == null || min_speed == null || min_speed === '') return true;
          return value >= min_speed;
        }),

      average_speed: Yup.number()
        .nullable()
        .transform((v, o) => (o === '' ? null : v))
        .typeError('Must be a number')
        .test('is-logical-average', 'Average Speed must be between Min and Max Speed', function (value) {
          const { min_speed, max_speed } = this.parent;
          if (value == null) return true; // Lewati jika kosong

          let isValid = true;
          if (min_speed != null && min_speed !== '') {
            isValid = isValid && value >= min_speed;
          }
          if (max_speed != null && max_speed !== '') {
            isValid = isValid && value <= max_speed;
          }
          return isValid;
        }),

      description: Yup.string().nullable()
    });

    const normalizeCompanyValue = (companyValue) => {
      if (companyValue === '' || companyValue === null || companyValue === undefined) return null;
      if (typeof companyValue === 'object') {
        if ('value' in companyValue) return Number(companyValue.value);
        if ('id' in companyValue) return Number(companyValue.id);
        return null;
      }
      const n = Number(companyValue);
      return Number.isFinite(n) ? Math.trunc(n) : null;
    };

    const handleSubmit = async (values, { setSubmitting }) => {
      setSubmitting(true);
      onSubmitStart();
      try {
        const companyId = normalizeCompanyValue(values.company);
        if (companyId === null) {
          throw new Error('Company id is missing or invalid');
        }
        const payload = {
          company: companyId,
          name: formatUpperCase(values.name) ?? '',
          length_dimension: values.length_dimension === '' ? null : Number(values.length_dimension),
          width_dimension: values.width_dimension === '' ? null : Number(values.width_dimension),
          height_dimension: values.height_dimension === '' ? null : Number(values.height_dimension),
          load_capacity: values.load_capacity === '' ? null : Number(values.load_capacity),
          max_speed: values.max_speed === '' ? null : Number(values.max_speed),
          min_speed: values.min_speed === '' ? null : Number(values.min_speed),
          average_speed: values.average_speed === '' ? null : Number(values.average_speed),
          description: values.description ?? ''
        };

        let response;
        if (id) {
          response = await updateVehicleType(id, payload);
          onSuccess && onSuccess('Updated successfully');
        } else {
          response = await createVehicleType(payload);
          onSuccess && onSuccess('Created successfully');
        }

        if (response?.code === 400 || response?.status === false || response?.error) {
          throw response;
        }

        onClose && onClose();
      } catch (err) {
        console.error('Submit error:', err);
        const errorMessage = err?.response?.data?.message || err?.data?.message || err?.message || 'Gagal menyimpan data';
        notify({ message: errorMessage, severity: 'error' });
      } finally {
        setSubmitting(false);
        onSubmitEnd();
      }
    };

    return (
      <Formik
        innerRef={formikRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur, handleSubmit, setFieldValue, isSubmitting }) => (
          <FormDialog
            open={open}
            onClose={onClose}
            title={id ? t('vehicleType.edit', 'Edit Vehicle Type') : t('vehicleType.add', 'Add Vehicle Type')}
            maxWidth="sm"
            fullHeight={false}
            actions={
              <>
                <Button
                  variant="contained"
                  type="submit"
                  form="vehicle-type-form"
                  disabled={isSubmitting || loading}
                  sx={{ backgroundColor: '#0072DA', textTransform: 'none' }}
                  startIcon={isSubmitting ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  {isSubmitting ? t('buttonTitle.processing', 'Processing...') : t('buttonTitle.save', 'Save')}
                </Button>
                <Button onClick={onClose} disableRipple disableElevation variant="outlined" sx={{ textTransform: 'none' }}>
                  {t('buttonTitle.cancel', 'Cancel')}
                </Button>
              </>
            }
          >
            {loading || companyPickerParams.isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4, minHeight: 200, alignItems: 'center' }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box sx={{ width: '100%' }}>
                <form id="vehicle-type-form" onSubmit={handleSubmit}>
                  <Grid container spacing={3}>
                    {/* KOLOM KIRI */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Stack spacing={2}>
                        <PickerField
                          label={t('general.company', 'Company')}
                          name="company"
                          value={values.company ?? null}
                          {...companyPickerParams}
                          allowAdd
                          AddComponent={MasterCompanyAddEdit}
                          onChange={(val) => {
                            const v = typeof val === 'object' && val !== null ? val.value : val;
                            setFieldValue('company', v);
                            try {
                              if (v !== null && v !== undefined && v !== '') {
                                localStorage.setItem('companyId', String(v));
                              }
                            } catch {}
                          }}
                          error={touched.company && !!errors.company}
                          helperText={touched.company && errors.company}
                          required
                        />

                        <TextField
                          fullWidth
                          size="medium"
                          id="name"
                          name="name"
                          label={t('vehicleType.name', 'Name')}
                          value={values.name}
                          onChange={(e) => {
                            setFieldValue('name', formatUpperCase(e.target.value));
                          }}
                          onBlur={handleBlur}
                          error={touched.name && !!errors.name}
                          helperText={touched.name && errors.name}
                          required
                          InputLabelProps={{
                            sx: {
                              '& .MuiFormLabel-asterisk': {
                                color: 'error.main'
                              }
                            }
                          }}
                        />

                        <Grid container spacing={2}>
                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="length_dimension"
                              name="length_dimension"
                              label={t('vehicleType.length', 'Length')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.length_dimension ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.length_dimension && !!errors.length_dimension}
                              helperText={touched.length_dimension && errors.length_dimension}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="width_dimension"
                              name="width_dimension"
                              label={t('vehicleType.width', 'Width')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.width_dimension ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.width_dimension && !!errors.width_dimension}
                              helperText={touched.width_dimension && errors.width_dimension}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="height_dimension"
                              name="height_dimension"
                              label={t('vehicleType.height', 'Height')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">cm</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.height_dimension ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.height_dimension && !!errors.height_dimension}
                              helperText={touched.height_dimension && errors.height_dimension}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="load_capacity"
                              name="load_capacity"
                              label={t('vehicleType.loadCapacity', 'Load Capacity')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">Kg</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.load_capacity ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.load_capacity && !!errors.load_capacity}
                              helperText={touched.load_capacity && errors.load_capacity}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Stack>
                    </Grid>

                    {/* KOLOM KANAN */}
                    <Grid item size={{ xs: 12, md: 6 }}>
                      <Stack spacing={2}>
                        <Grid container spacing={2}>
                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="min_speed"
                              name="min_speed"
                              label={t('vehicleType.minSpeed', 'Min Speed')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.min_speed ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.min_speed && !!errors.min_speed}
                              helperText={touched.min_speed && errors.min_speed}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>
                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="max_speed"
                              name="max_speed"
                              label={t('vehicleType.maxSpeed', 'Max Speed')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.max_speed ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.max_speed && !!errors.max_speed}
                              helperText={touched.max_speed && errors.max_speed}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item size={{ xs: 12 }}>
                            <TextField
                              fullWidth
                              size="medium"
                              id="average_speed"
                              name="average_speed"
                              label={t('vehicleType.averageSpeed', 'Average Speed')}
                              type="number"
                              InputProps={{ endAdornment: <InputAdornment position="end">km/h</InputAdornment> }}
                              inputProps={{ min: 0 }}
                              value={values.average_speed ?? ''}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              error={touched.average_speed && !!errors.average_speed}
                              helperText={touched.average_speed && errors.average_speed}
                              required
                              InputLabelProps={{
                                sx: {
                                  '& .MuiFormLabel-asterisk': {
                                    color: 'error.main'
                                  }
                                }
                              }}
                            />
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 1 }} />

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          id="description"
                          name="description"
                          label={t('general.description', 'Description')}
                          value={values.description ?? ''}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          error={touched.description && !!errors.description}
                          helperText={touched.description && errors.description}
                          required
                          InputLabelProps={{
                            sx: {
                              '& .MuiFormLabel-asterisk': {
                                color: 'error.main'
                              }
                            }
                          }}
                        />
                      </Stack>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            )}
          </FormDialog>
        )}
      </Formik>
    );
  }
);

export default VehicleTypeAddEdit;
